import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Divider,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  CircularProgress
} from '@material-ui/core';
import {
  Alert,
  AlertTitle
} from '@material-ui/lab';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  Timer as TimerIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon
} from '@material-ui/icons';
import { i18n } from '../../translate/i18n';
import { useKanbanAnalytics } from '../../hooks/useKanbanAnalytics';
import { format, subDays, isWithinInterval, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
  },
  statCard: {
    padding: theme.spacing(1.5),
    textAlign: 'center',
    height: '100%',
    minHeight: 120,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.grey[800] : '#fff',
    '&:hover': {
      boxShadow: theme.shadows[4],
      transform: 'translateY(-2px)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },
  statIcon: {
    fontSize: 32,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.primary.main,
  },
  statNumber: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
  statLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontWeight: 500,
    lineHeight: 1.2,
  },
  chartCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  chartTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 'bold',
  },
  performanceItem: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
  },
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  progressBar: {
    flex: 1,
    marginRight: theme.spacing(1),
  },
  progressText: {
    minWidth: 40,
    fontSize: '0.875rem',
  },
}));

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const KanbanAnalytics = ({ ticketId = null }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState('30');
  const { 
    analytics, 
    loading, 
    error, 
    fetchTasksAnalytics, 
    calculateTeamPerformance,
    generatePerformanceReport 
  } = useKanbanAnalytics(ticketId);
  const [performanceReport, setPerformanceReport] = useState(null);
  const [teamData, setTeamData] = useState([]);

  useEffect(() => {
    if (timeRange) {
      fetchTasksAnalytics({ dateRange: timeRange });
      loadPerformanceReport();
    }
  }, [timeRange, ticketId, fetchTasksAnalytics]);

  const loadPerformanceReport = async () => {
    try {
      const report = await generatePerformanceReport({ dateRange: timeRange });
      setPerformanceReport(report);
    } catch (error) {
      console.error('Error loading performance report:', error);
    }
  };

  // Processar dados de analytics para os gráficos
  const processAnalyticsData = () => {
    if (!analytics || !analytics.summary) return {};

    const statusLabels = {
      'todo': i18n.t('kanban.tasks.status.todo'),
      'in_progress': i18n.t('kanban.tasks.status.in_progress'),
      'done': i18n.t('kanban.tasks.status.done'),
      'blocked': i18n.t('kanban.tasks.status.blocked')
    };

    const priorityLabels = {
      'low': i18n.t('kanban.tasks.priority.low'),
      'medium': i18n.t('kanban.tasks.priority.medium'),
      'high': i18n.t('kanban.tasks.priority.high'),
      'urgent': i18n.t('kanban.tasks.priority.urgent')
    };

    const tasksByStatus = [
      { name: statusLabels.todo, value: analytics.summary.todoTasks || 0, status: 'todo' },
      { name: statusLabels.in_progress, value: analytics.summary.inProgressTasks || 0, status: 'in_progress' },
      { name: statusLabels.done, value: analytics.summary.completedTasks || 0, status: 'done' },
      { name: statusLabels.blocked, value: analytics.summary.blockedTasks || 0, status: 'blocked' }
    ].filter(item => item.value > 0);

    const tasksByPriority = Object.entries(analytics.priorityDistribution || {}).map(([priority, count]) => ({
      name: priorityLabels[priority] || priority,
      value: count,
      priority
    })).filter(item => item.value > 0);

    return {
      tasksByStatus,
      tasksByPriority,
      dailyStats: analytics.dailyStats || []
    };
  };

  const chartData = processAnalyticsData();

  const formatTime = (hours) => {
    if (hours < 1) {
      const minutes = Math.round(hours * 60);
      return `${minutes}min`;
    }
    return `${hours.toFixed(1)}h`;
  };

  const formatDate = (dateString) => {
    return format(parseISO(dateString), 'dd/MM', { locale: ptBR });
  };

  const renderOverviewCards = () => {
    if (!analytics?.summary) return null;
    
    return (
      <Grid container spacing={2} style={{ marginBottom: theme.spacing(3) }}>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card className={classes.statCard}>
            <AssignmentIcon className={classes.statIcon} />
            <Typography className={classes.statNumber}>
              {analytics.summary.totalTasks || 0}
            </Typography>
            <Typography className={classes.statLabel}>
              {i18n.t('kanban.analytics.totalTasks')}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card className={classes.statCard}>
            <CheckCircleIcon className={classes.statIcon} style={{ color: '#4caf50' }} />
            <Typography className={classes.statNumber}>
              {analytics.summary.completedTasks || 0}
            </Typography>
            <Typography className={classes.statLabel}>
              {i18n.t('kanban.analytics.completedTasks')}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card className={classes.statCard}>
            <TrendingUpIcon className={classes.statIcon} style={{ color: '#2196f3' }} />
            <Typography className={classes.statNumber}>
              {analytics.summary.completionRate || 0}%
            </Typography>
            <Typography className={classes.statLabel}>
              {i18n.t('kanban.analytics.completionRate')}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card className={classes.statCard}>
            <ScheduleIcon className={classes.statIcon} style={{ color: '#ff9800' }} />
            <Typography className={classes.statNumber}>
              {analytics.summary.overdueTasks || 0}
            </Typography>
            <Typography className={classes.statLabel}>
              {i18n.t('kanban.analytics.overdueTasks')}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card className={classes.statCard}>
            <TimerIcon className={classes.statIcon} style={{ color: '#9c27b0' }} />
            <Typography className={classes.statNumber}>
              {formatTime(analytics.timeTracking?.avgActualHours || 0)}
            </Typography>
            <Typography className={classes.statLabel}>
              {i18n.t('kanban.analytics.averageTime')}
            </Typography>
          </Card>
        </Grid>
      </Grid>
    );
  };

  if (error) {
    return (
      <Box className={classes.root}>
        <Alert severity="error">
          <AlertTitle>Erro ao carregar análises</AlertTitle>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={3}>
        <Typography variant="h4" component="h1">
          {i18n.t('kanban.analytics.title')}
        </Typography>
        <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
          <InputLabel>{i18n.t('kanban.analytics.timeRange')}</InputLabel>
          <Select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            label={i18n.t('kanban.analytics.timeRange')}
            disabled={loading}
          >
            <MenuItem value="7">{i18n.t('kanban.analytics.last7Days')}</MenuItem>
            <MenuItem value="30">{i18n.t('kanban.analytics.last30Days')}</MenuItem>
            <MenuItem value="90">{i18n.t('kanban.analytics.last90Days')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <>
          {renderOverviewCards()}

          {/* Recomendações de Performance */}
          {performanceReport?.recommendations && performanceReport.recommendations.length > 0 && (
            <Box mt={3} mb={3}>
              <Typography variant="h6" gutterBottom>
                {i18n.t('kanban.analytics.recommendations')}
              </Typography>
              {performanceReport.recommendations.map((rec, index) => (
                <Alert 
                  key={index} 
                  severity={rec.priority === 'high' ? 'warning' : 'info'}
                  style={{ marginBottom: 8 }}
                  icon={rec.priority === 'high' ? <WarningIcon /> : undefined}
                >
                  {rec.message}
                  {rec.count && ` (${rec.count} tarefas)`}
                </Alert>
              ))}
            </Box>
          )}

          <Grid container spacing={3} style={{ marginTop: 24 }}>
        {/* Tasks by Status Chart */}
        <Grid item xs={12} md={6}>
          <Card className={classes.chartCard}>
            <Typography variant="h6" className={classes.chartTitle}>
              {i18n.t('kanban.analytics.tasksByStatus')}
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData.tasksByStatus || []}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {(chartData.tasksByStatus || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Grid>

        {/* Tasks by Priority Chart */}
        <Grid item xs={12} md={6}>
          <Card className={classes.chartCard}>
            <Typography variant="h6" className={classes.chartTitle}>
              {i18n.t('kanban.analytics.tasksByPriority')}
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.tasksByPriority || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Grid>

        {/* Burndown Chart */}
        <Grid item xs={12}>
          <Card className={classes.chartCard}>
            <Typography variant="h6" className={classes.chartTitle}>
              {i18n.t('kanban.analytics.burndownChart')}
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData.dailyStats || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={formatDate} />
                <YAxis />
                <Tooltip labelFormatter={(value) => formatDate(value)} />
                <Line type="monotone" dataKey="created" stroke="#8884d8" name="Tarefas Criadas" />
                <Line type="monotone" dataKey="completed" stroke="#82ca9d" name={i18n.t('kanban.analytics.completedTasks')} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Grid>

        {/* Team Performance */}
        <Grid item xs={12} md={8}>
          <Card className={classes.chartCard}>
            <Typography variant="h6" className={classes.chartTitle}>
              {i18n.t('kanban.analytics.teamPerformance')}
            </Typography>
            {teamData.map((member, index) => (
              <Box key={member.user?.id || index} className={classes.performanceItem}>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar src={member.user?.profileImage}>
                      {member.user?.name?.charAt(0) || '?'}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={member.user?.name || 'Usuário Desconhecido'}
                    secondary={`${member.completedTasks || 0}/${member.totalTasks || 0} ${i18n.t('kanban.analytics.tasksCompleted')} • ${formatTime(member.totalActualHours || 0)} ${i18n.t('kanban.analytics.timeTracked')}`}
                  />
                  <Box className={classes.progressContainer}>
                    <LinearProgress
                      variant="determinate"
                      value={member.completionRate || 0}
                      className={classes.progressBar}
                      color={member.completionRate > 80 ? 'primary' : member.completionRate > 50 ? 'secondary' : 'inherit'}
                    />
                    <Typography className={classes.progressText}>
                      {Math.round(member.completionRate || 0)}%
                    </Typography>
                  </Box>
                </ListItem>
              </Box>
            ))}
            {teamData.length === 0 && (
              <Typography color="textSecondary" style={{ textAlign: 'center', padding: 20 }}>
                {i18n.t('kanban.analytics.noTeamData')}
              </Typography>
            )}
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={4}>
          <Card className={classes.chartCard}>
            <Typography variant="h6" className={classes.chartTitle}>
              {i18n.t('kanban.analytics.recentActivity')}
            </Typography>
            <List dense>
              {(chartData.dailyStats || []).slice(-10).map((stat, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={
                      <Typography variant="body2">
                        {formatDate(stat.date)}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          {stat.created} criadas • {stat.completed} concluídas
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
              {(!chartData.dailyStats || chartData.dailyStats.length === 0) && (
                <Typography color="textSecondary" style={{ textAlign: 'center', padding: 20 }}>
                  Nenhuma atividade recente
                </Typography>
              )}
            </List>
          </Card>
        </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default KanbanAnalytics;